import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { getToken, saveToken, removeToken } from './storage';
import { authAPI } from '../api/auth';
import { isCookieAuthMode } from './authMode';
import * as NavigationService from '../navigation/NavigationService';
import { logger } from './logger';

export { isCookieAuthMode } from './authMode';

/** Axios config for cookie auth (withCredentials) - merge when creating axios instances */
export const getAxiosAuthConfig = () =>
  isCookieAuthMode() ? { withCredentials: true } : {};

const AUTH_TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRES_KEY = 'token_expires';

// Track if we're currently refreshing to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Refresh the access token using the refresh token
 * @returns The new access token or null if refresh failed
 */
const refreshAccessToken = async (): Promise<string | null> => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      if (isCookieAuthMode()) {
        // Cookie mode: refresh token is in httpOnly cookie, no body needed
        logger.log('Refreshing token (cookie mode)...');
        const refreshResponse = await authAPI.refreshToken();
        if (!refreshResponse.success) {
          logger.error('Token refresh failed (cookie mode)');
          await handleTokenRefreshFailure();
          return null;
        }
        // New tokens set in cookies by backend; retry will use them
        logger.log('Token refresh OK (cookie mode)');
        return 'cookie'; // Sentinel - actual token in cookie
      }

      const refreshToken = await getToken(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        logger.log('No refresh token available, redirecting to login');
        await handleTokenRefreshFailure();
        return null;
      }

      logger.log('Refreshing token...');
      const refreshResponse = await authAPI.refreshToken(refreshToken);

      if (!refreshResponse.success || !refreshResponse.data) {
        logger.error('Token refresh failed');
        await handleTokenRefreshFailure();
        return null;
      }

      // Store new tokens
      const now = new Date().getTime();
      const newExpires = now + refreshResponse.data.expires_in * 1000;

      await Promise.all([
        saveToken(AUTH_TOKEN_KEY, refreshResponse.data.access_token),
        saveToken(REFRESH_TOKEN_KEY, refreshResponse.data.refresh_token),
        saveToken(TOKEN_EXPIRES_KEY, newExpires.toString()),
      ]);

      logger.log('Token refresh OK');
      return refreshResponse.data.access_token;
    } catch (error) {
      logger.error('Error refreshing token:', error);
      await handleTokenRefreshFailure();
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/** Public screens where we should NOT redirect to Login on session expiry */
const PUBLIC_SCREENS = new Set([
  'Landing',
  'Login',
  'Register',
  'ForgotPassword',
  'AuthCallback',
  'Onboarding',
  'VerifyEmailRequired',
  'PublicColorGenerator',
  'PublicExploreColours',
  'PublicColorAnalysis',
  'PublicVirtualDrape',
  'Invite',
  'Admin',
  'AdminLogin',
  'FAQ',
  'PrivacyPolicy',
  'TermsOfService',
  'BlogList',
  'BlogArticle',
]);

/**
 * Handle token refresh failure by clearing session and redirecting to login.
 * Skip redirect when already on a public page (e.g. Landing) - just clear session.
 */
const handleTokenRefreshFailure = async (): Promise<void> => {
  try {
    logger.log('Token refresh failed, logging out');

    if (isCookieAuthMode()) {
      await authAPI.logout();
    }

    // Clear all stored tokens
    await Promise.all([
      removeToken(AUTH_TOKEN_KEY),
      removeToken(REFRESH_TOKEN_KEY),
      removeToken(TOKEN_EXPIRES_KEY),
      removeToken('user_data'),
    ]);

    // Only redirect to Login when on a protected screen (not Landing or other public pages)
    // When route is unknown (e.g. nav not ready on initial load), don't redirect - stay put
    const currentRoute = NavigationService.getCurrentRoute();
    const routeName = currentRoute?.name as string | undefined;
    if (!routeName || PUBLIC_SCREENS.has(routeName)) {
      return; // Public page or nav not ready - don't redirect
    }

    setTimeout(() => {
      try {
        NavigationService.reset(0, [{ name: 'Login' }]);
      } catch (navError) {
        logger.error('❌ Navigation error:', navError);
        NavigationService.navigate('Login');
      }
    }, 100);
  } catch (error) {
    logger.error('❌ Error handling token refresh failure:', error);
  }
};

/**
 * Check if the access token is expired or about to expire (within 1 minute)
 */
const isTokenExpired = async (): Promise<boolean> => {
  try {
    const tokenExpires = await getToken(TOKEN_EXPIRES_KEY);
    if (!tokenExpires) {
      return true; // No expiration info, assume expired
    }

    const now = new Date().getTime();
    const expiresAt = parseInt(tokenExpires);
    const bufferTime = 60 * 1000; // 1 minute buffer

    return now >= expiresAt - bufferTime;
  } catch (error) {
    logger.error('Error checking token expiration:', error);
    return true; // On error, assume expired
  }
};

/**
 * Get a valid access token, refreshing if necessary
 * @returns The access token or null if unavailable
 */
export const getValidAccessToken = async (): Promise<string | null> => {
  try {
    if (isCookieAuthMode()) {
      return 'cookie'; // Tokens in httpOnly cookies; fetch uses credentials
    }

    const accessToken = await getToken(AUTH_TOKEN_KEY);

    if (!accessToken) {
      return null;
    }

    // Check if token is expired or about to expire
    if (await isTokenExpired()) {
      logger.log('Access token expired or about to expire, refreshing...');
      return await refreshAccessToken();
    }

    return accessToken;
  } catch (error) {
    logger.error('Error getting valid access token:', error);
    return null;
  }
};

/**
 * Create an axios response interceptor that handles token refresh on 401 errors
 */
export const createTokenRefreshInterceptor = (axiosInstance: AxiosInstance) => {
  const interceptorId = axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config;

      // If error is not 401 or we've already retried, reject
      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // Mark request as retried to prevent infinite loops
      originalRequest._retry = true;

      // Try to refresh the token
      const newAccessToken = await refreshAccessToken();

      if (!newAccessToken) {
        // Refresh failed, reject the original request
        return Promise.reject(error);
      }

      // Update auth: cookie mode uses cookies (no header); token mode needs Bearer
      if (newAccessToken !== 'cookie') {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      } else {
        delete originalRequest.headers.Authorization;
      }

      // Retry the original request
      return axiosInstance(originalRequest);
    }
  );

  return interceptorId;
};

/**
 * Enhanced apiFetch wrapper that handles token refresh automatically
 */
export const apiFetchWithTokenRefresh = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Get a valid access token (will refresh if needed)
  const accessToken = await getValidAccessToken();

  // Add authorization header if we have a token (not in cookie mode)
  const headers: HeadersInit = {
    ...((options.headers as Record<string, string>) || {}),
  };

  if (accessToken && accessToken !== 'cookie') {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Make the request
  const fetchOptions: RequestInit = {
    ...options,
    headers,
  };
  if (isCookieAuthMode()) {
    fetchOptions.credentials = 'include';
  }

  let response = await fetch(url, fetchOptions);

  // If we get a 401, try to refresh and retry once
  if (response.status === 401 && accessToken) {
    logger.log('Retrying with refreshed token...');
    const newAccessToken = await refreshAccessToken();

    if (newAccessToken) {
      const retryOptions: RequestInit = {
        ...options,
        headers: {
          ...((options.headers as Record<string, string>) || {}),
          ...(newAccessToken !== 'cookie'
            ? { Authorization: `Bearer ${newAccessToken}` }
            : {}),
        },
      };
      if (isCookieAuthMode()) {
        retryOptions.credentials = 'include';
      }

      response = await fetch(url, retryOptions);
    } else {
      // Refresh failed, user will be redirected to login
      const errorData = await response
        .json()
        .catch(() => ({ detail: 'Authentication failed' }));
      throw new Error(
        errorData.detail || 'Authentication failed. Please log in again.'
      );
    }
  }

  return response;
};
